import { getSessionId } from './session';
import { getBufferedLogEntriesSince, formatLogLine } from './logBufferStorage';
import { serializeLogData } from './sanitize';

const LINE_TIME_PATTERN = /^(\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2})/;
const MAX_LINE_CHARS = 8000;

function truncateLine(line) {
  if (line.length <= MAX_LINE_CHARS) {
    return line;
  }
  return `${line.slice(0, MAX_LINE_CHARS)}…`;
}

function parseLineTimestamp(line) {
  const match = String(line || '').match(LINE_TIME_PATTERN);
  if (!match) {
    return null;
  }
  const normalized = match[1].replace(' ', 'T');
  const parsed = Date.parse(normalized);
  return Number.isNaN(parsed) ? null : parsed;
}

function resolveEntryTimestamp(entry, lastKnownTs, nextKnownTs) {
  if (entry.timestamp) {
    return entry.timestamp;
  }
  const fromLine = parseLineTimestamp(entry.line);
  if (fromLine != null) {
    return fromLine;
  }
  if (lastKnownTs != null) {
    return lastKnownTs;
  }
  if (nextKnownTs != null) {
    return nextKnownTs;
  }
  return Date.now();
}

function formatBytes(bytes) {
  if (!bytes) {
    return '0 B';
  }
  const units = ['B', 'KB', 'MB', 'GB'];
  const index = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
  const value = bytes / (1024 ** index);
  return `${value.toFixed(index === 0 ? 0 : 2)} ${units[index]}`;
}

function formatBeijingDateTime(value) {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return date.toLocaleString('sv-SE', { timeZone: 'Asia/Shanghai' });
}

function buildConsoleLine(level, args) {
  const timestamp = new Date().toISOString().replace('T', ' ').slice(0, 19);
  const sessionId = getSessionId() || '-';
  const message = args.map((arg) => {
    if (arg == null) {
      return String(arg);
    }
    if (typeof arg === 'string') {
      return serializeLogData(arg);
    }
    try {
      return JSON.stringify(serializeLogData(arg));
    } catch (error) {
      return String(arg);
    }
  }).join(' ');
  return truncateLine(`${timestamp} [${level.toUpperCase()}] [sess:${sessionId}|trace:|user:] [Console] ${message}`);
}

export async function filterBufferedLogsByMinutes(minutes) {
  const cutoff = Date.now() - minutes * 60 * 1000;
  const entries = await getBufferedLogEntriesSince(cutoff - 60 * 1000);
  if (!entries.length) {
    return {
      entries: [],
      lineCount: 0,
      rawBytes: 0,
      logStartTime: null,
      logEndTime: null,
      text: '',
    };
  }

  const annotated = entries.map((entry, index) => {
    const nextEntry = entries[index + 1];
    const nextKnownTs = nextEntry?.timestamp || parseLineTimestamp(nextEntry?.line);
    return {
      entry,
      resolvedTs: resolveEntryTimestamp(entry, null, nextKnownTs),
    };
  });

  for (let i = 0; i < annotated.length; i += 1) {
    const prevTs = i > 0 ? annotated[i - 1].resolvedTs : null;
    annotated[i].resolvedTs = resolveEntryTimestamp(
      annotated[i].entry,
      prevTs,
      annotated[i].resolvedTs,
    );
  }

  const filtered = annotated
    .filter((item) => item.resolvedTs >= cutoff)
    .map((item) => item.entry);

  const lines = filtered.map((entry) => entry.line);
  const text = lines.length ? `${lines.join('\n')}\n` : '';
  const timestamps = annotated
    .filter((item) => item.resolvedTs >= cutoff)
    .map((item) => item.resolvedTs);

  const logStartTime = timestamps.length
    ? formatBeijingDateTime(Math.min(...timestamps))
    : null;
  const logEndTime = timestamps.length
    ? formatBeijingDateTime(Math.max(...timestamps))
    : null;

  return {
    entries: filtered,
    lineCount: filtered.length,
    rawBytes: new Blob([text]).size,
    logStartTime,
    logEndTime,
    text,
  };
}

export async function previewLogReport(minutes) {
  const filtered = await filterBufferedLogsByMinutes(minutes);
  let compressedBytes = 0;
  if (filtered.text) {
    compressedBytes = await estimateGzipSize(filtered.text);
  }
  const snapshot = {
    ...filtered,
    compressedBytes,
    rawBytesLabel: formatBytes(filtered.rawBytes),
    compressedBytesLabel: formatBytes(compressedBytes),
  };
  lastFilteredReportSnapshot = filtered;
  lastFilteredReportKey = reportSnapshotKey(minutes);
  lastFilteredReportAt = Date.now();
  return snapshot;
}

async function estimateGzipSize(text) {
  if (!text || typeof CompressionStream === 'undefined') {
    return Math.max(1, Math.round(text.length * 0.25));
  }
  const blob = new Blob([text]);
  const stream = blob.stream().pipeThrough(new CompressionStream('gzip'));
  const buffer = await new Response(stream).arrayBuffer();
  return buffer.byteLength;
}

function uint8ArrayToBase64(bytes) {
  if (!bytes?.length) {
    return '';
  }
  const chunkSize = 0x8000;
  let binary = '';
  for (let i = 0; i < bytes.length; i += chunkSize) {
    const slice = bytes.subarray(i, i + chunkSize);
    binary += String.fromCharCode.apply(null, slice);
  }
  return btoa(binary);
}

async function gzipTextToBase64(text) {
  if (!text) {
    return '';
  }
  if (typeof CompressionStream === 'undefined') {
    return btoa(unescape(encodeURIComponent(text)));
  }
  const blob = new Blob([text]);
  const stream = blob.stream().pipeThrough(new CompressionStream('gzip'));
  const buffer = await new Response(stream).arrayBuffer();
  return uint8ArrayToBase64(new Uint8Array(buffer));
}

let lastFilteredReportSnapshot = null;
let lastFilteredReportKey = '';
let lastFilteredReportAt = 0;
const REPORT_SNAPSHOT_TTL_MS = 90 * 1000;

function reportSnapshotKey(minutes) {
  return String(minutes);
}

export async function uploadLogReport({ description, minutes, guestId, prefiltered }) {
  const key = reportSnapshotKey(minutes);
  const snapshotFresh = lastFilteredReportSnapshot
    && lastFilteredReportKey === key
    && Date.now() - lastFilteredReportAt < REPORT_SNAPSHOT_TTL_MS;
  const filtered = prefiltered
    || (snapshotFresh ? lastFilteredReportSnapshot : await filterBufferedLogsByMinutes(minutes));
  if (!filtered.lineCount) {
    throw new Error('所选时间范围内没有可上传的日志');
  }

  const payload = {
    sessionId: getSessionId(),
    guestId: guestId || undefined,
    description: description?.trim() || '',
    timeRangeMinutes: minutes,
    logStartTime: filtered.logStartTime,
    logEndTime: filtered.logEndTime,
    lineCount: filtered.lineCount,
    rawBytes: filtered.rawBytes,
    device: {
      userAgent: navigator.userAgent,
      url: window.location.href,
      appVersion: import.meta.env.VITE_APP_VERSION || 'unknown',
      language: navigator.language,
      screen: `${window.screen.width}x${window.screen.height}`,
    },
    contentGzipBase64: await gzipTextToBase64(filtered.text),
  };

  // 动态加载 myRequest：它依赖 logger，而 logger 又依赖本文件，
  // 静态 import 会形成 logger ↔ myRequest 循环依赖（HMR 时可能报
  // "Cannot access 'logger' before initialization"）。
  const { default: myRequest } = await import('@/utils/myRequest');
  const result = await myRequest.post('/api/client-logs/report', payload);
  return result;
}

export function buildConsoleLogLine(level, args) {
  return buildConsoleLine(level, args);
}

export { formatLogLine };
